import moment from 'moment';
import { diffLines, diffWords } from 'diff';
import DOMPurify from 'dompurify';

export const formatMinutes = (minutes: number): string => {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return `${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')}`;
};

//00:00:00| 00:00
export const formatTimeBySecond = (totalSeconds: number) => {
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  const pad = (n: number) => String(n).padStart(2, '0');

  if (hours > 0) {
    return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
  }

  return `${pad(minutes)}:${pad(seconds)}`;
};

export const clamp = (value: number, min: number, max: number): number => {
  return Math.max(min, Math.min(value, max));
};

export const formatDatetime = (_date: string | Date, formatBy?: string) => {
  return moment(new Date(_date)).format(formatBy ? formatBy : 'DD/MM/YYYY');
};

export const formatTime = (_date: Date, formatBy?: string) => {
  return moment(new Date(_date)).format(formatBy ? formatBy : 'HH:mm');
};

export const cloneDatetime = (_date: string | Date, hours: number) => {
  return moment(new Date(_date)).clone().add(hours, 'hours');
};

export const getNowIso = () => new Date().toISOString();

export const parseNumber = (value: unknown): number | null => {
  if (value == null) return null;
  if (typeof value === 'number') return value;

  const parsed = Number.parseFloat(String(value));
  return Number.isNaN(parsed) ? null : parsed;
};

function renderWordDiff(oldStr: string, newStr: string) {
  const wordDiff = diffWords(oldStr, newStr);

  return wordDiff
    .map(part => {
      const value = escapeHtml(part.value);

      if (part.added) {
        return `<ins class="diff-add">${value}</ins>`;
      }

      if (part.removed) {
        return `<del class="diff-remove">${value}</del>`;
      }
      return value;
    })
    .join('');
}

export function buildDiffHtml(oldHtml: string, newHtml: string) {
  const oldText = htmlToText(oldHtml);
  const newText = htmlToText(newHtml);

  const lineDiff = diffLines(oldText, newText);

  const result: string[] = [];

  for (let i = 0; i < lineDiff.length; i++) {
    const part = lineDiff[i];

    if (!part.added && !part.removed) {
      result.push(escapeHtml(part.value));
      continue;
    }

    if (part.removed && lineDiff[i + 1]?.added) {
      result.push(renderWordDiff(part.value, lineDiff[i + 1].value));
      i++;
      continue;
    }

    if (part.removed) {
      result.push(`<del class="diff-remove">${escapeHtml(part.value)}</del>`);
      continue;
    }

    if (part.added) {
      result.push(`<ins class="diff-add">${escapeHtml(part.value)}</ins>`);
    }
  }

  return result.join('').replace(/\n/g, '<br/>');
}

export type TextSelectionOffsets = {
  from: number;
  to: number;
  quote: string;
};

//Lấy nội dung 1 đoạn text trên dom
export const getSelectionOffsets = (root: HTMLElement): TextSelectionOffsets | null => {
  const selection = window.getSelection();
  if (!selection || selection.rangeCount === 0 || selection.isCollapsed) return null;

  const range = selection.getRangeAt(0);
  if (!root.contains(range.commonAncestorContainer)) return null;

  const startRange = range.cloneRange();
  startRange.selectNodeContents(root);
  startRange.setEnd(range.startContainer, range.startOffset);

  const endRange = range.cloneRange();
  endRange.selectNodeContents(root);
  endRange.setEnd(range.endContainer, range.endOffset);

  const rawQuote = range.toString();
  const quote = rawQuote.trim();
  const leadingWhitespace = rawQuote.length - rawQuote.trimStart().length;
  const trailingWhitespace = rawQuote.length - rawQuote.trimEnd().length;
  const from = startRange.toString().length + leadingWhitespace;
  const to = endRange.toString().length - trailingWhitespace;

  if (!quote || from === to) return null;

  return { from, to, quote };
};

//tạo 1 range trên DOM với from-to
export const createRangeFromOffsets = (root: HTMLElement, from: number, to: number) => {
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  let currentNode: Node | null = walker.nextNode();
  let offset = 0;
  let startNode: Text | null = null;
  let endNode: Text | null = null;
  let startOffset = 0;
  let endOffset = 0;

  while (currentNode) {
    const textNode = currentNode as Text;
    const text = textNode.textContent ?? '';
    const nextOffset = offset + text.length;

    if (!startNode && from >= offset && from <= nextOffset) {
      startNode = textNode;
      startOffset = from - offset;
    }

    if (!endNode && to >= offset && to <= nextOffset) {
      endNode = textNode;
      endOffset = to - offset;
      break;
    }

    offset = nextOffset;
    currentNode = walker.nextNode();
  }

  if (!startNode || !endNode) return null;

  const range = document.createRange();
  range.setStart(startNode, startOffset);
  range.setEnd(endNode, endOffset);
  return range;
};

// làm sạch đoạn html, loại bỏ các tag không cho phép
export const sanitizeHtml = (html: string) => {
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: [
      'b', 'i', 'p', 'br', 'ul', 'ol', 'li', 'img', 'ins', 'del', 'span',
      'strong', 'em', 'table', 'thead', 'tbody', 'tr', 'th', 'td', 'figure',
      'figcaption', 'a',
    ],
    ALLOWED_ATTR: [
      'src', 'alt', 'class', 'href', 'target', 'rel', 'data-blank-index',
    ],
  });
};

export const countWordsFromHtml = (html: string): number => {
  const text = htmlToText(html);
  const trimmed = text.trim();
  if (!trimmed) return 0;

  return trimmed.split(/\s+/).length;
};

export const htmlToText = (html: string): string => {
  if (!html) return '';

  const normalizedHtml = html
    .replace(/<\/p>/gi, '\n')
    .replace(/<\/div>/gi, '\n')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/&nbsp;/g, ' ');

  let text = '';
  if (typeof DOMParser !== 'undefined') {
    try {
      const doc = new DOMParser().parseFromString(normalizedHtml, 'text/html');
      text = doc.body.textContent || '';
    } catch (e) {
      text = normalizedHtml.replace(/<[^>]+>/g, ' ');
    }
  } else {
    text = normalizedHtml.replace(/<[^>]+>/g, ' ');
  }

  return text.replace(/\n+/g, '\n').trim();
};

// replace plain text để an toàn thành html
export const escapeHtml = (value: string) => value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

//dựng html từ plain text nhiều đoạn
export const toHtml = (text: string) =>
  text
    .split('\n\n')
    .map(paragraph => `<p>${escapeHtml(paragraph)}</p>`)
    .join('');

